import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as classesService from "../services/classes";
import * as resourcesService from "../services/resources";
import * as studentsService from "../services/students";
import StudentShell from "../components/StudentShell/StudentShell";
import "./Resources.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export default function Resources() {
  const { user, accessToken, effectiveRole, activeStudentId } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = useCallback(
    (key, options) => {
      try {
        return t(key, options);
      } catch {
        return options?.defaultValue || key;
      }
    },
    [t],
  );

  const canManage = useMemo(
    () => ["teacher", "admin", "superadmin"].includes(effectiveRole),
    [effectiveRole],
  );

  const shouldFetchClasses = useMemo(() => {
    const role = user?.role;
    return ["teacher", "admin", "superadmin", "parent", "student"].includes(
      role,
    );
  }, [user?.role]);

  const isParentViewingStudent = useMemo(
    () =>
      user?.role === "parent" &&
      effectiveRole === "student" &&
      Boolean(activeStudentId),
    [activeStudentId, effectiveRole, user?.role],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resources, setResources] = useState([]);
  const [classes, setClasses] = useState([]);
  const [directStudents, setDirectStudents] = useState([]);

  const [assignmentTarget, setAssignmentTarget] = useState("class");
  const [createForm, setCreateForm] = useState({
    title: "",
    text: "",
    class_group_id: "",
    student_id: "",
  });
  const [createFiles, setCreateFiles] = useState([]);
  const [createBusy, setCreateBusy] = useState(false);

  const allStudents = useMemo(() => {
    const result = [];
    const seen = new Set();
    asArray(classes).forEach((g) => {
      asArray(g.students).forEach((s) => {
        const id = Number(s?.id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        result.push(s);
      });
    });
    // Also include any students fetched directly that aren't in a class
    asArray(directStudents).forEach((s) => {
      const id = Number(s?.id);
      if (!id || seen.has(id)) return;
      seen.add(id);
      result.push(s);
    });
    result.sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    return result;
  }, [classes, directStudents]);

  const classNameById = useMemo(() => {
    const m = new Map();
    asArray(classes).forEach((g) => {
      m.set(Number(g.id), g.name);
    });
    return m;
  }, [classes]);

  const studentNameById = useMemo(() => {
    const m = new Map();
    allStudents.forEach((s) => {
      m.set(
        Number(s.id),
        s.full_name || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
      );
    });
    return m;
  }, [allStudents]);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    try {
      const promises = [
        resourcesService.fetchResources({ accessToken }),
        canManage
          ? studentsService
              .fetchStudents({ accessToken })
              .catch(() => ({ students: [] }))
          : Promise.resolve({ students: [] }),
      ];
      if (shouldFetchClasses) {
        promises.unshift(classesService.fetchClasses({ accessToken }));
      }

      const results = await Promise.all(promises);
      const classesPayload = shouldFetchClasses ? results[0] : { classes: [] };
      const resourcesPayload = shouldFetchClasses ? results[1] : results[0];
      const studentsPayload = shouldFetchClasses ? results[2] : results[1];

      setClasses(asArray(classesPayload?.classes));
      setResources(asArray(resourcesPayload?.resources));
      setDirectStudents(asArray(studentsPayload?.students));
    } catch (e) {
      setError(
        e?.message ||
          tFn("resources.failedLoad", {
            defaultValue: "Failed to load resources",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, canManage, shouldFetchClasses, tFn]);

  useEffect(() => {
    load();
  }, [load]);

  const resetCreateForm = () => {
    setAssignmentTarget("class");
    setCreateForm({ title: "", text: "", class_group_id: "", student_id: "" });
    setCreateFiles([]);
  };

  const doCreate = async (e) => {
    e?.preventDefault?.();
    if (!accessToken) return;

    const title = (createForm.title || "").trim();
    const text = (createForm.text || "").trim();

    const class_group_id =
      assignmentTarget === "class" ? createForm.class_group_id : "";
    const student_id =
      assignmentTarget === "student" ? createForm.student_id : "";

    if (!title) {
      setError(
        tFn("resources.titleRequired", { defaultValue: "Title is required" }),
      );
      return;
    }

    if (!class_group_id && !student_id) {
      setError(
        tFn("resources.targetRequired", {
          defaultValue: "Please choose a class or a student",
        }),
      );
      return;
    }

    setCreateBusy(true);
    setError("");
    try {
      const created = await resourcesService.createResource(accessToken, {
        title,
        text,
        class_group_id: class_group_id ? Number(class_group_id) : undefined,
        student_id: student_id ? Number(student_id) : undefined,
      });

      const files = Array.isArray(createFiles) ? createFiles : [];
      if (created?.id && files.length) {
        await resourcesService.uploadResourceAttachments({
          accessToken,
          resourceId: created.id,
          files,
        });
      }

      resetCreateForm();
      await load();
    } catch (e2) {
      setError(
        e2?.message ||
          tFn("resources.failedCreate", {
            defaultValue: "Failed to create resource",
          }),
      );
    } finally {
      setCreateBusy(false);
    }
  };

  const doDelete = async (res) => {
    if (!accessToken) return;
    if (
      !window.confirm(
        tFn("resources.confirmDelete", {
          defaultValue: "Delete this resource?",
        }),
      )
    )
      return;

    setError("");
    try {
      await resourcesService.deleteResource(accessToken, res.id);
      setResources((prev) => prev.filter((x) => x.id !== res.id));
    } catch (e) {
      setError(
        e?.message ||
          tFn("resources.failedDelete", {
            defaultValue: "Failed to delete resource",
          }),
      );
    }
  };

  const targetLabel = (res) => {
    if (res?.class_group_id) {
      return (
        classNameById.get(Number(res.class_group_id)) ||
        tFn("resources.targetClass", { defaultValue: "Class" })
      );
    }
    if (res?.student_id) {
      return (
        studentNameById.get(Number(res.student_id)) ||
        tFn("resources.targetStudent", { defaultValue: "Student" })
      );
    }
    return "";
  };

  const visibleResources = useMemo(() => {
    const list = asArray(resources);
    if (!isParentViewingStudent) return list;

    const activeId = Number(activeStudentId);
    if (!activeId) return list;

    const classIdsForActive = new Set(
      asArray(classes)
        .filter((g) => asArray(g?.student_ids).map(Number).includes(activeId))
        .map((g) => Number(g.id)),
    );

    return list.filter((res) => {
      if (res?.student_id && Number(res.student_id) === activeId) return true;
      if (
        res?.class_group_id &&
        classIdsForActive.has(Number(res.class_group_id))
      )
        return true;
      return false;
    });
  }, [activeStudentId, classes, isParentViewingStudent, resources]);

  if (loading) {
    const page = (
      <div className="resources-page" dir={dir}>
        <div className="container resources-page__container">
          <p>{tFn("resources.loading", { defaultValue: "Loading..." })}</p>
        </div>
      </div>
    );

    return effectiveRole === "student" ? (
      <StudentShell>{page}</StudentShell>
    ) : (
      page
    );
  }

  const page = (
    <div className="resources-page" dir={dir}>
      <div className="container resources-page__container">
        <header className="resources-page__header">
          <h1 className="resources-page__title">
            {tFn("resources.title", { defaultValue: "Resources" })}
          </h1>
          <p className="resources-page__subtitle">
            {canManage
              ? tFn("resources.subtitleTeacher", {
                  defaultValue:
                    "Create resources and assign them to a class or a student.",
                })
              : tFn("resources.subtitleStudent", {
                  defaultValue: "Resources assigned to you will appear here.",
                })}
          </p>
        </header>

        {error ? <div className="resources-page__error">{error}</div> : null}

        {canManage ? (
          <section className="resources-card">
            <h2 className="resources-card__title">
              {tFn("resources.create", { defaultValue: "Create resource" })}
            </h2>

            <form className="resources-form" onSubmit={doCreate}>
              <label className="resources-form__full">
                <span>
                  {tFn("resources.fields.title", {
                    defaultValue: "Title",
                  })}
                </span>
                <input
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  placeholder={tFn("resources.fields.titlePlaceholder", {
                    defaultValue: "e.g. Week 3 material",
                  })}
                />
              </label>

              <label className="resources-form__full">
                <span>
                  {tFn("resources.fields.text", {
                    defaultValue: "Text",
                  })}
                </span>
                <textarea
                  rows={4}
                  value={createForm.text}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, text: e.target.value }))
                  }
                  placeholder={tFn("resources.fields.textPlaceholder", {
                    defaultValue: "Optional description or instructions",
                  })}
                />
              </label>

              <label>
                <span>
                  {tFn("resources.fields.assignTo", {
                    defaultValue: "Assign to",
                  })}
                </span>
                <select
                  value={assignmentTarget}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAssignmentTarget(next);
                    setCreateForm((p) => ({
                      ...p,
                      class_group_id: "",
                      student_id: "",
                    }));
                  }}
                >
                  <option value="class">
                    {tFn("resources.assign.class", {
                      defaultValue: "Class",
                    })}
                  </option>
                  <option value="student">
                    {tFn("resources.assign.student", {
                      defaultValue: "Student",
                    })}
                  </option>
                </select>
              </label>

              {assignmentTarget === "class" ? (
                <label>
                  <span>
                    {tFn("resources.fields.class", {
                      defaultValue: "Class",
                    })}
                  </span>
                  <select
                    value={createForm.class_group_id}
                    onChange={(e) =>
                      setCreateForm((p) => ({
                        ...p,
                        class_group_id: e.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {tFn("resources.pickClass", {
                        defaultValue: "Select a class",
                      })}
                    </option>
                    {asArray(classes).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  <span>
                    {tFn("resources.fields.student", {
                      defaultValue: "Student",
                    })}
                  </span>
                  <select
                    value={createForm.student_id}
                    onChange={(e) =>
                      setCreateForm((p) => ({
                        ...p,
                        student_id: e.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {tFn("resources.pickStudent", {
                        defaultValue: "Select a student",
                      })}
                    </option>
                    {allStudents.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.full_name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="resources-form__full">
                <span>
                  {tFn("resources.fields.attachments", {
                    defaultValue: "Attachments",
                  })}
                </span>
                <input
                  type="file"
                  multiple
                  onChange={(e) =>
                    setCreateFiles(Array.from(e.target.files || []))
                  }
                />
              </label>

              <div className="resources-form__actions">
                <button className="btn" type="submit" disabled={createBusy}>
                  {createBusy
                    ? tFn("resources.creating", {
                        defaultValue: "Creating...",
                      })
                    : tFn("resources.create", {
                        defaultValue: "Create resource",
                      })}
                </button>
              </div>
            </form>
          </section>
        ) : null}

        <section className="resources-card">
          <h2 className="resources-card__title">
            {tFn("resources.listTitle", { defaultValue: "Resources" })}
          </h2>

          {visibleResources.length === 0 ? (
            <p className="resources-empty">
              {tFn("resources.empty", {
                defaultValue: "No resources yet.",
              })}
            </p>
          ) : (
            <div className="resources-list">
              {visibleResources.map((res) => (
                <article key={res.id} className="resources-item">
                  <div className="resources-item__head">
                    <div>
                      <h3 className="resources-item__title">{res.title}</h3>
                      <p className="resources-item__meta">
                        {targetLabel(res)}
                        {res?.teacher?.full_name
                          ? ` • ${res.teacher.full_name}`
                          : ""}
                      </p>
                    </div>

                    {canManage && res?.teacher?.id === user?.id ? (
                      <div className="resources-item__actions">
                        <button
                          className="btn btn--ghost"
                          type="button"
                          onClick={() => doDelete(res)}
                        >
                          {tFn("resources.delete", {
                            defaultValue: "Delete",
                          })}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {res.text ? (
                    <p className="resources-item__text">{res.text}</p>
                  ) : null}

                  {asArray(res.attachments).length ? (
                    <ul className="resources-item__attachments">
                      {asArray(res.attachments).map((att) => (
                        <li key={att.id}>
                          <a href={att.url} target="_blank" rel="noreferrer">
                            {att.name || att.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );

  return effectiveRole === "student" ? (
    <StudentShell>{page}</StudentShell>
  ) : (
    page
  );
}
